import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { renderToStaticMarkup } from 'react-dom/server';
import ReactMarkdown from 'react-markdown';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
    PiArrowCounterClockwiseBold,
    PiCheckBold,
    PiClockCounterClockwiseBold,
    PiEyeBold,
    PiFilePdfBold,
    PiFloppyDiskBold,
    PiGearSixBold,
    PiLockSimpleBold,
    PiMagnifyingGlassBold,
    PiPencilSimpleBold,
    PiPlusBold,
    PiSealCheckBold,
    PiSquaresFourBold,
    PiTrashBold,
    PiUsersThreeBold,
    PiXBold,
} from 'react-icons/pi';
import { Badge, Button, Input, PageHeader, Skeleton, Switch } from '@v2/design-system';
import V2Modal from '@v2/components/ui/V2Modal';
import api from '@v2/lib/api';
import { cn } from '@v2/utils/cn';
import { useNavigationMeta } from '@v2/hooks/useResourceCrud';
import DocContent from '@v2/features/company-docs/DocContent';
import DocEditor from '@v2/features/company-docs/DocEditor';
import { DOC_ICONS, docIcon } from '@v2/features/company-docs/docIcons';
import { DOCUMENT_CATEGORIES } from '@v2/types/intranet';
import type { CompanyDocument, CompanyDocumentVersion, FeatureLink } from '@v2/types/intranet';

type ApiError = { response?: { data?: { message?: string; errors?: Record<string, string[]> } } };

const errorMessage = (err: unknown, fallback: string) => {
    const data = (err as ApiError)?.response?.data;
    const first = data?.errors ? Object.values(data.errors)[0]?.[0] : null;
    return first ?? data?.message ?? fallback;
};

function DocIconTile({ icon, size = 'md' }: { icon?: string | null; size?: 'md' | 'lg' }) {
    const meta = docIcon(icon);
    const Icon = meta.icon;
    return (
        <span className={cn('flex shrink-0 items-center justify-center rounded-xl', meta.tone, size === 'lg' ? 'size-14' : 'size-11')}>
            <Icon className={size === 'lg' ? 'size-8' : 'size-6'} />
        </span>
    );
}

const STATUS_CHIP: Record<CompanyDocumentVersion['status'], { label: string; cls: string }> = {
    approved: { label: 'Publicada', cls: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300' },
    pending: { label: 'Aguardando aprovação', cls: 'bg-amber-50 text-amber-800 dark:bg-amber-500/15 dark:text-amber-200' },
    rejected: { label: 'Recusada', cls: 'bg-rose-50 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300' },
};

// ─── Lista ────────────────────────────────────────────────────────────────────

export function DocumentsPage() {
    const [search, setSearch] = useState('');
    // Chave própria: a Home guarda só o array em ['intranet', 'documents'].
    const { data, isLoading } = useQuery({
        queryKey: ['intranet', 'documents', 'index'],
        queryFn: () =>
            api
                .get<{ data: CompanyDocument[]; meta: { can_manage: boolean } }>('/intranet/documents')
                .then((r) => r.data),
    });
    const items = data?.data ?? [];
    const term = search.trim().toLowerCase();
    const filtered = term
        ? items.filter((d) => `${d.title} ${d.description ?? ''} ${d.category_label}`.toLowerCase().includes(term))
        : items;
    const groups = DOCUMENT_CATEGORIES.map((c) => ({ ...c, docs: filtered.filter((d) => d.category === c.value) })).filter(
        (g) => g.docs.length > 0,
    );

    return (
        <div className="space-y-6">
            <PageHeader
                title="Documentos"
                subtitle="Manuais, processos e regras da empresa — sempre na versão mais recente."
                breadcrumb={[{ name: 'Empresa' }, { name: 'Documentos' }]}
            >
                {data?.meta?.can_manage && (
                    <Link to="/company/documents/new">
                        <Button>
                            <PiPlusBold className="me-1.5 size-4" />
                            Novo documento
                        </Button>
                    </Link>
                )}
            </PageHeader>

            <div className="relative max-w-xl">
                <PiMagnifyingGlassBold className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar documento…" className="ps-9" />
            </div>

            {isLoading ? (
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                    {[0, 1, 2].map((i) => (
                        <Skeleton key={i} className="h-28 rounded-2xl" />
                    ))}
                </div>
            ) : groups.length === 0 ? (
                <p className="rounded-2xl border border-dashed border-gray-300 px-6 py-14 text-center text-sm text-gray-500 dark:border-gray-600">
                    {term ? `Nada encontrado para “${search}”.` : 'Nenhum documento publicado.'}
                </p>
            ) : (
                groups.map((group) => (
                    <section key={group.value}>
                        <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">{group.label}</h2>
                        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                            {group.docs.map((d) => (
                                <Link
                                    key={d.id}
                                    to={`/company/documents/${d.id}`}
                                    className="group flex gap-3 rounded-2xl border border-gray-200 bg-white p-4 transition hover:border-primary/50 hover:shadow-sm dark:border-gray-700 dark:bg-gray-800"
                                >
                                    <DocIconTile icon={d.icon} />
                                    <div className="min-w-0 flex-1">
                                        <p className="font-semibold text-gray-900 group-hover:text-primary dark:text-gray-100">{d.title}</p>
                                        {d.description && <p className="mt-0.5 line-clamp-2 text-xs text-gray-500 dark:text-gray-400">{d.description}</p>}
                                        <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px] text-gray-400">
                                            {d.current_version ? <span className="font-mono">v{d.current_version} ·</span> : null}
                                            <span>{d.updated_at_label ?? d.updated_at}</span>
                                            {d.restricted && (
                                                <span className="inline-flex items-center gap-0.5 rounded-full bg-gray-100 px-1.5 py-0.5 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                                                    <PiLockSimpleBold className="size-3" /> Restrito
                                                </span>
                                            )}
                                            {!d.published && (
                                                <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-gray-600 dark:bg-gray-700 dark:text-gray-300">Rascunho</span>
                                            )}
                                            {(d.pending_count ?? 0) > 0 && (
                                                <span className="rounded-full bg-amber-100 px-1.5 py-0.5 font-semibold text-amber-800 dark:bg-amber-500/20 dark:text-amber-200">
                                                    {d.pending_count} para aprovar
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </section>
                ))
            )}
        </div>
    );
}

// ─── Leitura ──────────────────────────────────────────────────────────────────

function useDocument(id?: string) {
    return useQuery({
        queryKey: ['intranet', 'documents', id],
        enabled: Boolean(id),
        queryFn: () => api.get<{ data: CompanyDocument }>(`/intranet/documents/${id}`).then((r) => r.data.data),
    });
}

function tocFrom(html?: string | null): { level: number; text: string; index: number }[] {
    if (!html) return [];
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return Array.from(doc.querySelectorAll('h2, h3')).map((h, index) => ({
        level: h.tagName === 'H2' ? 2 : 3,
        text: h.textContent?.trim() ?? '',
        index,
    }));
}

function InfoRow({ label, children }: { label: string; children: ReactNode }) {
    return (
        <div>
            <dt className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">{label}</dt>
            <dd className="mt-0.5 text-xs text-gray-700 dark:text-gray-200">{children}</dd>
        </div>
    );
}

function RoleChips({ roles, empty }: { roles?: string[] | null; empty: string }) {
    if (!roles || roles.length === 0) return <span className="text-gray-500">{empty}</span>;
    return (
        <span className="flex flex-wrap gap-1">
            {roles.map((r) => (
                <span key={r} className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[11px] dark:bg-gray-700">
                    {r}
                </span>
            ))}
        </span>
    );
}

export function DocumentDetailPage() {
    const { id } = useParams<{ id: string }>();
    const [params, setParams] = useSearchParams();
    const { data: doc, isLoading, isError } = useDocument(id);
    const historyOpen = params.get('history') === '1';
    const toc = useMemo(() => tocFrom(doc?.content_html), [doc?.content_html]);

    const setHistory = (open: boolean) => {
        const next = new URLSearchParams(params);
        if (open) next.set('history', '1');
        else next.delete('history');
        setParams(next, { replace: true });
    };

    const scrollTo = (index: number) => {
        const heads = document.querySelectorAll('.doc-read h2, .doc-read h3');
        heads[index]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    if (isLoading) {
        return (
            <div className="space-y-4">
                <Skeleton className="h-16 rounded-2xl" />
                <Skeleton className="h-96 rounded-2xl" />
            </div>
        );
    }
    if (isError || !doc) {
        return (
            <p className="rounded-2xl border border-dashed border-gray-300 px-6 py-14 text-center text-sm text-gray-500">
                Documento não encontrado ou sem acesso. <Link to="/company/documents" className="text-primary underline">Voltar aos documentos</Link>
            </p>
        );
    }

    const showPending = (doc.pending_count ?? 0) > 0 && (doc.can_edit || doc.can_approve);

    return (
        <div className="space-y-4">
            <PageHeader
                title={doc.title}
                breadcrumb={[{ name: 'Empresa' }, { name: 'Documentos', href: '/company/documents' }, { name: doc.title }]}
            >
                <div className="flex flex-wrap gap-2">
                    <Button variant="outline" onClick={() => window.open(`/company/documents/${doc.id}/print`, '_blank', 'noopener')}>
                        <PiFilePdfBold className="me-1.5 size-4" />
                        PDF
                    </Button>
                    <Button variant="outline" onClick={() => setHistory(true)}>
                        <PiClockCounterClockwiseBold className="me-1.5 size-4" />
                        Histórico
                    </Button>
                    {doc.can_edit && (
                        <Link to={`/company/documents/${doc.id}/edit`}>
                            <Button>
                                <PiPencilSimpleBold className="me-1.5 size-4" />
                                Editar
                            </Button>
                        </Link>
                    )}
                </div>
            </PageHeader>

            {showPending && (
                <button
                    type="button"
                    onClick={() => setHistory(true)}
                    className="flex w-full items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-left text-sm text-amber-900 dark:border-amber-700/50 dark:bg-amber-500/10 dark:text-amber-200"
                >
                    <PiSealCheckBold className="size-5 shrink-0" />
                    {doc.pending_count} alteração(ões) aguardando aprovação.{' '}
                    <span className="font-semibold underline">{doc.can_approve ? 'Revisar' : 'Ver'}</span>
                </button>
            )}

            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_17rem]">
                <article className="min-w-0 rounded-2xl border border-gray-200 bg-white p-6 sm:p-8 dark:border-gray-700 dark:bg-gray-800">
                    <div className="mb-6 flex items-start gap-4">
                        <DocIconTile icon={doc.icon} size="lg" />
                        <div className="min-w-0">
                            <Badge size="sm" rounded="pill" variant="flat">
                                {doc.category_label}
                            </Badge>
                            {doc.description && <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">{doc.description}</p>}
                        </div>
                    </div>
                    <div className="doc-read">
                        {doc.content_html || doc.content_markdown ? (
                            <DocContent html={doc.content_html} markdown={doc.content_markdown} />
                        ) : (
                            <p className="py-10 text-center text-sm text-gray-400">Sem conteúdo ainda.</p>
                        )}
                    </div>
                </article>

                <aside className="space-y-3 lg:sticky lg:top-20 lg:self-start">
                    {toc.length > 1 && (
                        <nav className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
                            <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-gray-400">Neste documento</p>
                            <ul className="max-h-72 space-y-1 overflow-y-auto">
                                {toc.map((h) => (
                                    <li key={h.index}>
                                        <button
                                            type="button"
                                            onClick={() => scrollTo(h.index)}
                                            className={cn(
                                                'w-full truncate text-left text-xs text-gray-600 hover:text-primary dark:text-gray-300',
                                                h.level === 3 && 'ps-3 text-[11px] text-gray-500',
                                            )}
                                        >
                                            {h.text}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </nav>
                    )}

                    {(doc.feature_links ?? []).length > 0 && (
                        <div className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
                            <p className="mb-2 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                                <PiSquaresFourBold className="size-3.5" /> Telas do Hub citadas
                            </p>
                            <div className="flex flex-col gap-1">
                                {doc.feature_links!.map((l) => (
                                    <Link key={l.path + l.label} to={l.path} className="truncate rounded-lg px-2 py-1 text-xs font-medium text-primary hover:bg-primary/5">
                                        → {l.label}
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}

                    <dl className="space-y-3 rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
                        <InfoRow label="Versão">
                            <span className="font-mono">v{doc.current_version ?? 0}</span>
                            <button type="button" onClick={() => setHistory(true)} className="ms-2 text-primary hover:underline">
                                ver histórico
                            </button>
                        </InfoRow>
                        <InfoRow label="Última alteração">
                            {doc.updated_by ?? '—'} · {doc.updated_at_label}
                        </InfoRow>
                        <InfoRow label="Criado por">
                            {doc.created_by ?? '—'} · {doc.created_at_label}
                        </InfoRow>
                        <InfoRow label="Quem pode ver">
                            <RoleChips roles={doc.access?.view} empty="Todos os usuários do Hub" />
                        </InfoRow>
                        <InfoRow label="Quem pode editar">
                            <RoleChips roles={doc.access?.edit} empty="Só administradores de documentos" />
                        </InfoRow>
                        <InfoRow label="Aprovação para mudar">
                            {doc.requires_approval ? (
                                <RoleChips roles={doc.access?.approve} empty="Super Admin" />
                            ) : (
                                <span className="text-gray-500">Não precisa — publica ao salvar</span>
                            )}
                        </InfoRow>
                        {doc.can_manage && (
                            <Link
                                to={`/company/documents/${doc.id}/edit#settings`}
                                className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                            >
                                <PiGearSixBold className="size-3.5" /> Acesso e aprovação
                            </Link>
                        )}
                    </dl>
                </aside>
            </div>

            <HistoryModal doc={doc} open={historyOpen} onClose={() => setHistory(false)} />
        </div>
    );
}

// ─── Histórico / aprovação ────────────────────────────────────────────────────

function HistoryModal({ doc, open, onClose }: { doc: CompanyDocument; open: boolean; onClose: () => void }) {
    const qc = useQueryClient();
    const [viewing, setViewing] = useState<CompanyDocumentVersion | null>(null);
    const [rejecting, setRejecting] = useState<CompanyDocumentVersion | null>(null);
    const [note, setNote] = useState('');

    const versionsQuery = useQuery({
        queryKey: ['intranet', 'documents', String(doc.id), 'versions'],
        enabled: open,
        queryFn: () => api.get<{ data: CompanyDocumentVersion[] }>(`/intranet/documents/${doc.id}/versions`).then((r) => r.data.data),
    });

    const versionQuery = useQuery({
        queryKey: ['intranet', 'documents', String(doc.id), 'versions', viewing?.id],
        enabled: viewing !== null,
        queryFn: () =>
            api.get<{ data: CompanyDocumentVersion }>(`/intranet/documents/${doc.id}/versions/${viewing!.id}`).then((r) => r.data.data),
    });

    const refresh = () => {
        void qc.invalidateQueries({ queryKey: ['intranet', 'documents'] });
    };

    const action = useMutation({
        mutationFn: async ({ v, kind, body }: { v: CompanyDocumentVersion; kind: 'approve' | 'reject' | 'restore'; body?: object }) =>
            (await api.post<{ message: string }>(`/intranet/documents/${doc.id}/versions/${v.id}/${kind}`, body ?? {})).data,
        onSuccess: (data) => {
            toast.success(data.message);
            setRejecting(null);
            setViewing(null);
            setNote('');
            refresh();
        },
        onError: (err) => toast.error(errorMessage(err, 'Não foi possível concluir.')),
    });

    const versions = versionsQuery.data ?? [];

    return (
        <>
            <V2Modal open={open && !viewing && !rejecting} onClose={onClose} title="Histórico de versões" description={doc.title} size="2xl">
                {versionsQuery.isLoading ? (
                    <Skeleton className="h-40 rounded-xl" />
                ) : (
                    <ol className="relative space-y-3 border-s-2 border-gray-100 ps-5 dark:border-gray-700">
                        {versions.map((v) => {
                            const chip = STATUS_CHIP[v.status];
                            return (
                                <li key={v.id} className="relative">
                                    <span
                                        className={cn(
                                            'absolute -start-[1.72rem] top-1.5 size-3 rounded-full ring-4 ring-white dark:ring-gray-900',
                                            v.is_current ? 'bg-emerald-500' : v.status === 'pending' ? 'bg-amber-400' : v.status === 'rejected' ? 'bg-rose-400' : 'bg-gray-300',
                                        )}
                                    />
                                    <div className="rounded-xl border border-gray-200 p-3 dark:border-gray-700">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <span className="font-mono text-sm font-bold">v{v.version}</span>
                                            <span className={cn('rounded-full px-2 py-0.5 text-[11px] font-semibold', chip.cls)}>
                                                {v.is_current ? 'Versão atual' : chip.label}
                                            </span>
                                            <span className="ms-auto text-[11px] text-gray-400">{v.created_at_label}</span>
                                        </div>
                                        <p className="mt-1 text-sm text-gray-800 dark:text-gray-100">{v.change_summary}</p>
                                        <p className="mt-0.5 text-[11px] text-gray-500">
                                            Por <strong>{v.created_by ?? '—'}</strong>
                                            {v.reviewed_by && v.status !== 'pending' && v.reviewed_by !== v.created_by && (
                                                <>
                                                    {' '}· {v.status === 'rejected' ? 'recusada' : 'aprovada'} por <strong>{v.reviewed_by}</strong> em {v.reviewed_at_label}
                                                </>
                                            )}
                                        </p>
                                        {v.review_note && <p className="mt-1 rounded-lg bg-gray-50 px-2 py-1 text-[11px] italic text-gray-600 dark:bg-gray-800 dark:text-gray-300">“{v.review_note}”</p>}
                                        <div className="mt-2 flex flex-wrap gap-1.5">
                                            <Button size="sm" variant="outline" onClick={() => setViewing(v)}>
                                                <PiEyeBold className="me-1 size-3.5" /> Ver
                                            </Button>
                                            {v.can_review && (
                                                <>
                                                    <Button size="sm" isLoading={action.isPending} onClick={() => action.mutate({ v, kind: 'approve' })}>
                                                        <PiCheckBold className="me-1 size-3.5" /> Aprovar e publicar
                                                    </Button>
                                                    <Button size="sm" variant="outline" onClick={() => setRejecting(v)}>
                                                        <PiXBold className="me-1 size-3.5" /> Recusar
                                                    </Button>
                                                </>
                                            )}
                                            {doc.can_edit && v.status === 'approved' && !v.is_current && (
                                                <Button size="sm" variant="outline" isLoading={action.isPending} onClick={() => action.mutate({ v, kind: 'restore' })}>
                                                    <PiArrowCounterClockwiseBold className="me-1 size-3.5" /> Restaurar
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                    </ol>
                )}
            </V2Modal>

            <V2Modal
                open={viewing !== null}
                onClose={() => setViewing(null)}
                title={viewing ? `v${viewing.version} — ${viewing.title}` : ''}
                description={viewing ? `${viewing.created_by ?? '—'} · ${viewing.created_at_label} · ${viewing.change_summary}` : undefined}
                size="5xl"
                footer={
                    <Button variant="outline" onClick={() => setViewing(null)}>
                        Voltar ao histórico
                    </Button>
                }
            >
                {versionQuery.isLoading ? <Skeleton className="h-64 rounded-xl" /> : <DocContent html={versionQuery.data?.content_html} />}
            </V2Modal>

            <V2Modal
                open={rejecting !== null}
                onClose={() => setRejecting(null)}
                title={`Recusar v${rejecting?.version ?? ''}`}
                description="O autor recebe o motivo no sininho."
                size="md"
                footer={
                    <>
                        <Button variant="outline" onClick={() => setRejecting(null)}>
                            Cancelar
                        </Button>
                        <Button
                            disabled={note.trim().length < 3}
                            isLoading={action.isPending}
                            onClick={() => rejecting && action.mutate({ v: rejecting, kind: 'reject', body: { note } })}
                        >
                            Recusar alteração
                        </Button>
                    </>
                }
            >
                <textarea
                    autoFocus
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Motivo (ex.: o prazo correto é D+3)"
                    className="min-h-[90px] w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900"
                />
            </V2Modal>
        </>
    );
}

// ─── Edição ───────────────────────────────────────────────────────────────────

type RoleOption = { value: string; label: string };

function RolePicker({ options, value, onChange, emptyLabel }: { options: RoleOption[]; value: string[]; onChange: (v: string[]) => void; emptyLabel: string }) {
    const toggle = (role: string) => onChange(value.includes(role) ? value.filter((r) => r !== role) : [...value, role]);
    return (
        <div>
            <div className="flex flex-wrap gap-1.5">
                {options.map((o) => {
                    const active = value.includes(o.value);
                    return (
                        <button
                            key={o.value}
                            type="button"
                            onClick={() => toggle(o.value)}
                            aria-pressed={active}
                            className={cn(
                                'rounded-full border px-2.5 py-1 text-xs transition',
                                active
                                    ? 'border-primary bg-primary/10 font-semibold text-primary'
                                    : 'border-gray-200 text-gray-600 hover:border-gray-300 dark:border-gray-700 dark:text-gray-300',
                            )}
                        >
                            {o.label}
                        </button>
                    );
                })}
            </div>
            <p className="mt-1 text-[11px] text-gray-400">{value.length === 0 ? emptyLabel : `${value.length} perfil(is) selecionado(s)`}</p>
        </div>
    );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
    return (
        <label className="block">
            <span className="mb-1 block text-xs font-semibold text-gray-700 dark:text-gray-200">{label}</span>
            {children}
            {hint && <span className="mt-1 block text-[11px] text-gray-400">{hint}</span>}
        </label>
    );
}

export function DocumentEditPage() {
    const { id } = useParams<{ id: string }>();
    const isNew = !id;
    const navigate = useNavigate();
    const qc = useQueryClient();
    const { data: doc, isLoading } = useDocument(id);
    const nav = useNavigationMeta();

    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [html, setHtml] = useState<string | null>(isNew ? '' : null);
    const [links, setLinks] = useState<FeatureLink[]>([]);
    const [summary, setSummary] = useState('');
    const [category, setCategory] = useState('process');
    const [icon, setIcon] = useState<string | null>('guide');
    const [viewRoles, setViewRoles] = useState<string[]>([]);
    const [editorRoles, setEditorRoles] = useState<string[]>([]);
    const [requiresApproval, setRequiresApproval] = useState(false);
    const [approverRoles, setApproverRoles] = useState<string[]>([]);
    const [settingsDirty, setSettingsDirty] = useState(false);

    const canManage = isNew || Boolean(doc?.can_manage);
    const optionsQuery = useQuery({
        queryKey: ['intranet', 'documents', 'options'],
        enabled: canManage,
        queryFn: () => api.get<{ data: { roles: RoleOption[] } }>('/intranet/documents/options').then((r) => r.data.data),
    });

    useEffect(() => {
        if (!doc) return;
        setTitle(doc.title);
        setDescription(doc.description ?? '');
        // Documento antigo em markdown vira HTML na primeira edição.
        setHtml(doc.content_html ?? (doc.content_markdown ? renderToStaticMarkup(<ReactMarkdown>{doc.content_markdown}</ReactMarkdown>) : ''));
        setLinks(doc.feature_links ?? []);
        setCategory(doc.category);
        setIcon(doc.icon ?? null);
        setViewRoles(doc.view_roles ?? []);
        setEditorRoles(doc.editor_roles ?? []);
        setRequiresApproval(Boolean(doc.requires_approval));
        setApproverRoles(doc.approver_roles ?? []);
    }, [doc]);

    useEffect(() => {
        if (window.location.hash === '#settings') {
            window.setTimeout(() => document.getElementById('settings')?.scrollIntoView({ behavior: 'smooth' }), 300);
        }
    }, [isLoading]);

    const willNeedApproval = !isNew && Boolean(doc?.requires_approval) && !doc?.can_approve;
    const settings = {
        category,
        icon,
        view_roles: viewRoles,
        editor_roles: editorRoles,
        requires_approval: requiresApproval,
        approver_roles: approverRoles,
    };

    const save = useMutation({
        mutationFn: async () => {
            const content = { title: title.trim(), description: description.trim() || null, content_html: html ?? '', feature_links: links };
            if (isNew) {
                const { data } = await api.post<{ data: CompanyDocument }>('/intranet/documents/create', {
                    ...content,
                    ...settings,
                    change_summary: summary.trim() || 'Criação do documento',
                });
                return { id: data.data.id, message: 'Documento criado.' };
            }
            if (canManage && settingsDirty) {
                await api.put(`/intranet/documents/${id}/settings`, settings);
            }
            const { data } = await api.post<{ message: string }>(`/intranet/documents/${id}/versions`, {
                ...content,
                change_summary: summary.trim(),
            });
            return { id: Number(id), message: data.message };
        },
        onSuccess: ({ id: docId, message }) => {
            toast.success(message);
            void qc.invalidateQueries({ queryKey: ['intranet', 'documents'] });
            navigate(`/company/documents/${docId}`);
        },
        onError: (err) => toast.error(errorMessage(err, 'Não foi possível salvar.')),
    });

    const saveSettingsOnly = useMutation({
        mutationFn: async () => api.put(`/intranet/documents/${id}/settings`, settings),
        onSuccess: () => {
            toast.success('Acesso e aprovação atualizados.');
            setSettingsDirty(false);
            void qc.invalidateQueries({ queryKey: ['intranet', 'documents'] });
        },
        onError: (err) => toast.error(errorMessage(err, 'Não foi possível salvar.')),
    });

    const remove = useMutation({
        mutationFn: async () => api.delete(`/intranet/documents/${id}`),
        onSuccess: () => {
            toast.success('Documento removido.');
            void qc.invalidateQueries({ queryKey: ['intranet', 'documents'] });
            navigate('/company/documents');
        },
    });

    const markSettings = <T,>(setter: (v: T) => void) => (v: T) => {
        setter(v);
        setSettingsDirty(true);
    };

    const missing = !title.trim() ? 'Informe o título.' : !isNew && summary.trim().length < 3 ? 'Descreva o que mudou (vai para o histórico).' : null;
    const navItems = (nav.data ?? []).flatMap((g) => g.items.map((i) => ({ ...i, group: g.label })));

    if (!isNew && (isLoading || html === null)) {
        return <Skeleton className="h-[32rem] rounded-2xl" />;
    }
    if (!isNew && doc && !doc.can_edit) {
        return <p className="py-14 text-center text-sm text-gray-500">Você não tem permissão para editar este documento.</p>;
    }

    return (
        <div className="space-y-4">
            <PageHeader
                title={isNew ? 'Novo documento' : `Editar: ${doc?.title}`}
                breadcrumb={[
                    { name: 'Empresa' },
                    { name: 'Documentos', href: '/company/documents' },
                    ...(doc ? [{ name: doc.title, href: `/company/documents/${doc.id}` }] : []),
                    { name: isNew ? 'Novo' : 'Editar' },
                ]}
            />

            {willNeedApproval && (
                <p className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-900 dark:border-amber-700/50 dark:bg-amber-500/10 dark:text-amber-200">
                    <PiSealCheckBold className="size-5 shrink-0" />
                    Este documento exige aprovação: sua alteração vai para revisão e só aparece depois de aprovada.
                </p>
            )}

            <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_20rem]">
                <div className="min-w-0 space-y-3">
                    <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Título do documento" className="text-lg font-semibold" />
                    <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Resumo em uma frase (aparece na lista)" />
                    <DocEditor value={html ?? ''} onChange={setHtml} title={title} documentId={doc?.id} />
                </div>

                <aside className="space-y-3">
                    <div className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
                        <p className="mb-2 flex items-center gap-1 text-xs font-semibold text-gray-700 dark:text-gray-200">
                            <PiSquaresFourBold className="size-4" /> Telas do Hub relacionadas
                        </p>
                        <div className="space-y-1">
                            {links.map((l, i) => (
                                <div key={l.path + i} className="flex items-center gap-1 rounded-lg bg-gray-50 px-2 py-1 text-xs dark:bg-gray-900">
                                    <span className="min-w-0 flex-1 truncate">{l.label}</span>
                                    <span className="truncate font-mono text-[10px] text-gray-400">{l.path}</span>
                                    <button type="button" onClick={() => setLinks(links.filter((_, j) => j !== i))} aria-label="Remover" className="text-gray-400 hover:text-rose-600">
                                        <PiXBold className="size-3.5" />
                                    </button>
                                </div>
                            ))}
                        </div>
                        <select
                            value=""
                            onChange={(e) => {
                                const item = navItems.find((n) => n.href === e.target.value);
                                if (!item) return;
                                const path = item.href.replace(/^https?:\/\/[^/]+/, '').replace(/^\/v2(?=\/|$)/, '') || '/';
                                if (!links.some((l) => l.path === path)) setLinks([...links, { label: item.name, path }]);
                            }}
                            className="mt-2 h-9 w-full rounded-lg border border-gray-200 bg-white px-2 text-xs dark:border-gray-700 dark:bg-gray-900"
                        >
                            <option value="">+ Adicionar tela…</option>
                            {(nav.data ?? []).map((g) => (
                                <optgroup key={g.group} label={g.label}>
                                    {g.items.map((i) => (
                                        <option key={i.href} value={i.href}>
                                            {i.name}
                                        </option>
                                    ))}
                                </optgroup>
                            ))}
                        </select>
                        <p className="mt-1 text-[11px] text-gray-400">Aparecem ao lado do documento como atalho.</p>
                    </div>

                    {canManage && (
                        <div id="settings" className="space-y-4 rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
                            <p className="flex items-center gap-1 text-xs font-semibold text-gray-700 dark:text-gray-200">
                                <PiGearSixBold className="size-4" /> Organização, acesso e aprovação
                            </p>
                            <Field label="Categoria">
                                <select
                                    value={category}
                                    onChange={(e) => markSettings(setCategory)(e.target.value)}
                                    className="h-9 w-full rounded-lg border border-gray-200 bg-white px-2 text-sm dark:border-gray-700 dark:bg-gray-900"
                                >
                                    {DOCUMENT_CATEGORIES.map((c) => (
                                        <option key={c.value} value={c.value}>
                                            {c.label}
                                        </option>
                                    ))}
                                </select>
                            </Field>
                            <Field label="Ícone">
                                <div className="flex flex-wrap gap-1.5">
                                    {DOC_ICONS.map((i) => {
                                        const Icon = i.icon;
                                        return (
                                            <button
                                                key={i.key}
                                                type="button"
                                                title={i.label}
                                                onClick={() => markSettings(setIcon)(i.key)}
                                                className={cn('flex size-9 items-center justify-center rounded-lg', i.tone, icon === i.key && 'ring-2 ring-primary ring-offset-1 dark:ring-offset-gray-800')}
                                            >
                                                <Icon className="size-5" />
                                            </button>
                                        );
                                    })}
                                </div>
                            </Field>
                            <Field label="Quem pode ver">
                                <RolePicker options={optionsQuery.data?.roles ?? []} value={viewRoles} onChange={markSettings(setViewRoles)} emptyLabel="Nenhum marcado = todos os usuários do Hub." />
                            </Field>
                            <Field label="Quem pode editar">
                                <RolePicker
                                    options={optionsQuery.data?.roles ?? []}
                                    value={editorRoles}
                                    onChange={markSettings(setEditorRoles)}
                                    emptyLabel="Nenhum marcado = só administradores de documentos."
                                />
                            </Field>
                            <div className="flex items-start justify-between gap-3">
                                <span>
                                    <span className="block text-xs font-semibold text-gray-700 dark:text-gray-200">Mudanças precisam de aprovação</span>
                                    <span className="block text-[11px] text-gray-400">A versão nova só é publicada quando outra pessoa aprova.</span>
                                </span>
                                <Switch
                                    size="sm"
                                    checked={requiresApproval}
                                    onChange={(e) => markSettings(setRequiresApproval)(e.target.checked)}
                                    aria-label="Mudanças precisam de aprovação"
                                    className="shrink-0 [&_label]:my-0"
                                />
                            </div>
                            {requiresApproval && (
                                <Field label="Quem aprova">
                                    <RolePicker
                                        options={optionsQuery.data?.roles ?? []}
                                        value={approverRoles}
                                        onChange={markSettings(setApproverRoles)}
                                        emptyLabel="Nenhum marcado = só o Super Admin aprova."
                                    />
                                </Field>
                            )}
                            {!isNew && settingsDirty && (
                                <Button size="sm" variant="outline" className="w-full" isLoading={saveSettingsOnly.isPending} onClick={() => saveSettingsOnly.mutate()}>
                                    Salvar só acesso e aprovação
                                </Button>
                            )}
                            {!isNew && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (window.confirm('Remover este documento e todo o histórico de versões?')) remove.mutate();
                                    }}
                                    className="inline-flex items-center gap-1 text-[11px] text-rose-600 hover:underline"
                                >
                                    <PiTrashBold className="size-3.5" /> Remover documento
                                </button>
                            )}
                        </div>
                    )}
                    {!canManage && (
                        <p className="flex items-start gap-1.5 rounded-2xl border border-gray-200 bg-white p-4 text-[11px] text-gray-500 dark:border-gray-700 dark:bg-gray-800">
                            <PiUsersThreeBold className="mt-0.5 size-4 shrink-0" />
                            Acesso e aprovação são definidos pelos administradores de documentos.
                        </p>
                    )}
                </aside>
            </div>

            <div className="sticky bottom-0 z-20 -mx-4 border-t border-gray-200 bg-white/95 px-4 py-3 backdrop-blur sm:-mx-6 sm:px-6 dark:border-gray-700 dark:bg-gray-900/95">
                <div className="flex flex-wrap items-center gap-3">
                    <input
                        value={summary}
                        onChange={(e) => setSummary(e.target.value)}
                        maxLength={500}
                        placeholder={isNew ? 'Resumo da criação (opcional)' : 'O que mudou? (obrigatório — vai para o histórico)'}
                        className="h-10 min-w-[14rem] flex-1 rounded-lg border border-gray-200 bg-white px-3 text-sm dark:border-gray-700 dark:bg-gray-900"
                    />
                    {missing && <span className="text-xs text-gray-500">{missing}</span>}
                    <Button variant="outline" onClick={() => navigate(doc ? `/company/documents/${doc.id}` : '/company/documents')}>
                        Cancelar
                    </Button>
                    <Button disabled={missing !== null} isLoading={save.isPending} onClick={() => save.mutate()}>
                        <PiFloppyDiskBold className="me-1.5 size-4" />
                        {isNew ? 'Criar documento' : willNeedApproval ? 'Enviar para aprovação' : 'Publicar nova versão'}
                    </Button>
                </div>
            </div>
        </div>
    );
}
